// i18next-extract-mark-ns-start Step1
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { isMultilingual } from '../../../languages';
import OrderSummaryCloth from './OrderSummaryCloth';
import { capitalizeWords, formattedName } from '../../services/formatting';
import { formatPrice } from '../../services/pricing';
import { tabletThresholdPixels, colors } from '../home/v4/styledComponents';
import mondialRelayLogo from '../../assets/order/mondialRelayLogoBlack.png';
import colissimoLogo from '../../assets/order/colissimoLogoBlack.png';
import depositLogo from '../../assets/order/deposit.icon.black.svg';

const TotalRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-weight: 700;
  font-size: 18px;
  text-transform: uppercase;
  margin: 32px 0;
  padding: ${({ vertical }) => vertical ? '0 20px' : '0 17px'};
  ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
  ${({ grey }) => grey && `color: ${colors.grey200}; `}
  @media (max-width: ${tabletThresholdPixels}) {
    margin: 20px 0px;
    ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
    padding: 0 12px;
  }
`;
const LittleTotalRow = styled(TotalRow)`
  font-size: 16px;
  text-transform: uppercase;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 15px;
  }
`;
const Line = styled.div`
  border-width: 0.5px;
  border-style: solid;
  margin: ${(props) => props.marginTop}px 0px ${(props) => props.marginBottom}px;
  @media (max-width: ${tabletThresholdPixels}) {
    margin: 20px 0px 0px;
  }
`;
const AddressAndSlotsContainer = styled.div`
  margin-top: 32px;
  ${({ vertical }) => vertical && 'padding: 0 20px;'};
  @media (max-width: ${tabletThresholdPixels}) {
    margin-top: 20px;
    ${({ vertical }) => vertical && 'padding: 0 10px;'};
  }
`;
const PiecesContainer = styled.div`
  padding: ${({ vertical }) => vertical ? '0 20px' : '0 17px'};
  @media (max-width: ${tabletThresholdPixels}) {
    padding: 0 12px;
  }
`;
const TransporterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 5px 0 0;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 12px;
  }
`;
const Transporter = styled.div`
  font-size: 16px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  gap: 14px;
`;
const Relay = styled.div`
  display: flex;
  font-size: 14px;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 12px;
  }
`;
const Text = styled.p`
  font-size: 14px;
  margin: 0;
`;
const CardsContainer = styled.div`
  margin-top: 25px;
  @media (max-width: ${tabletThresholdPixels}) {
    margin-top: 20px;
  }
`;
const SubtitleContainer = styled.div`
  background-color: ${({ ocher }) => ocher ? `${colors.beige700}` : `${colors.grey600}`};
  border-radius: 4px;
  padding: 10px;
  ${({ vertical }) => vertical && `background-color: ${colors.black}`};
`;
const Subtitle2 = styled.p`
  font-size:   ${({ vertical }) => vertical ? 16 : 22}px;
  text-transform: uppercase;
  color: ${colors.beige100};
  margin: 0;
`;
const Note = styled.p`
  font-size: 12px;
  text-align: end;
  margin: 0px;
  padding: 0 20px;
  @media (max-width: ${tabletThresholdPixels}) {
    padding: 0 10px;
  }
`;

const getPackagePriceWithDiscount = (transporter, deliveryFee, transporterInsuranceAmount) => {
  const deliveryFeeWithoutInsurance = deliveryFee.packageFee - transporterInsuranceAmount * 2;
  if (transporter === 'MONDIAL_RELAY' && isMultilingual) return (deliveryFeeWithoutInsurance / 2);
  if (transporter === 'COLISSIMO_SIGNATURE') return deliveryFeeWithoutInsurance;
  return deliveryFee.packageFee;
};

export default function OrderSummaryPackage({
  selectedAddress, relayPoint, transporter, vertical, selectedDates, clothesPackage, hasDefinedPrice, totalClothes, hasUpcyclingWithoutPriceRange,
  promoCode, deliveryFee, isDoubleProcess, rawClothes, deleteCloth, duplicateCloth, editingClothIndex, setEditingClothIndex,
  isAdviceOrder, pricingInverse, discounts, lastStep, isOnlyRemaillage, transporterInsurance, currency,
}) {
  const { t } = useTranslation();
  const transporterInsuranceAmount = transporterInsurance ? transporterInsurance.amount : 0;

  const renderMondialRelay = () => (
    <TransporterContainer>
      <Transporter>
        <img src={mondialRelayLogo} alt="Mondial Relay" />
        <b>{t('Mondial Relay')}</b>
      </Transporter>
      <Relay>
        <b>{t('Dépôt : ')}</b>
        <div>
          <Text>{relayPoint.name}</Text>
          <Text>{`${capitalizeWords(relayPoint.street)} ${'à'} ${formattedName(relayPoint.locality)}`}</Text>
        </div>
      </Relay>
    </TransporterContainer>
  );

  const renderColissimo = () => (
    <TransporterContainer>
      <Transporter>
        <img src={colissimoLogo} alt="Colissimo" />
        <b>{t('Colissimo')}</b>
      </Transporter>
      <Text>{t('Déposez votre colis au bureau de poste le plus proche de chez vous.')}</Text>
    </TransporterContainer>
  );

  const renderDeposit = () => (
    <TransporterContainer>
      <Transporter>
        <img src={depositLogo} alt="Dépôt en atelier" />
        <b>{t('Dépôt en atelier')}</b>
      </Transporter>
      <Relay>
        <b>{('Adresse : ')}</b>
        {isOnlyRemaillage ? (
          <div>
            <Text>1 rue Henry Monnier</Text>
            <Text>75009 Paris</Text>
          </div>
        ) : (
          <div>
            <Text>10 rue Notre Dame de Lorette</Text>
            <Text>75009 Paris</Text>
          </div>
        )}
      </Relay>
    </TransporterContainer>
  );

  const renderInsuranceFee = () => (
    <>
      <LittleTotalRow marginBottom="0">
        {t('Assurance')}
        {' '}
        <span>
          {formatPrice(transporterInsurance.amount * 2, currency)}
        </span>
      </LittleTotalRow>
      <Note>
        {formatPrice(transporterInsurance.amount, currency)}
        {' '}
        {t('par trajet')}
      </Note>
    </>
  );

  const renderPackageFee = (packageFee) => (
    <>
      <LittleTotalRow marginBottom="0">
        {t('Frais de livraison')}
        {' '}
        <span>
          {promoCode.doesTilliOfferDeliveryFees ? t('Offerts') : formatPrice(packageFee, currency)}
        </span>
      </LittleTotalRow>
      {(packageFee) > 0
        && (
          <Note>
            {formatPrice((packageFee / 2), currency)}
            {' '}
            {t('par trajet')}
          </Note>
        )}
    </>
  );

  const renderSubTotal = () => {
    if (!hasDefinedPrice) {
      return (
        <LittleTotalRow>
          {t('Sous-Total')}
          {' '}
          <span>{t('Estimation en RDV')}</span>
        </LittleTotalRow>
      );
    }
    return (
      <LittleTotalRow>
        {t('Sous-Total')}
        {' '}
        <span>
          {formatPrice(totalClothes, currency)}
          {hasUpcyclingWithoutPriceRange ? t('+ estimation en RDV') : ''}
        </span>
      </LittleTotalRow>
    );
  };

  const renderPieces = () => clothesPackage.map((cloth) => {
    const priceRange = cloth.upcyclingItems.reduce((acc, upcyclingItem) => acc + (upcyclingItem.priceRange || 0), 0);
    return (
      <OrderSummaryCloth
        key={`${cloth.index}_${cloth.name}_${cloth.slug}`}
        discounts={discounts}
        cloth={cloth}
        clothes={rawClothes}
        deleteCloth={deleteCloth}
        duplicateCloth={duplicateCloth}
        editingClothIndex={editingClothIndex}
        priceRange={priceRange}
        setEditingClothIndex={setEditingClothIndex}
        isAdviceOrder={isAdviceOrder}
        pricingInverse={pricingInverse}
        vertical={vertical}
        lastStep={lastStep}
        currency={currency}
      />
    );
  });
  return (
    <>
      <CardsContainer ocher vertical={vertical}>
        <SubtitleContainer ocher vertical={vertical}>
          <Subtitle2 vertical={vertical}>{t('Envoi de colis')}</Subtitle2>
        </SubtitleContainer>
        {vertical
    && (
      <>
        <AddressAndSlotsContainer vertical={vertical}>
          {relayPoint.name && transporter === 'MONDIAL_RELAY' && renderMondialRelay()}
          {['COLISSIMO', 'COLISSIMO_SIGNATURE'].includes(transporter) && renderColissimo()}
          {transporter === 'DEPOSIT' && renderDeposit()}
        </AddressAndSlotsContainer>
        {((selectedAddress && Object.keys(selectedDates).length > 0) || transporter !== null) && <Line marginTop={25} marginBottom={25} />}
      </>
    )}
        <PiecesContainer vertical={vertical}>
          {renderPieces()}
        </PiecesContainer>
      </CardsContainer>
      {(vertical && !isDoubleProcess) && renderSubTotal()}
      {(vertical && transporter !== 'DEPOSIT') && renderPackageFee(getPackagePriceWithDiscount(transporter, deliveryFee, transporterInsuranceAmount))}
      {(vertical && transporterInsurance) && renderInsuranceFee()}
    </>
  );
}

OrderSummaryPackage.propTypes = {
  selectedAddress: PropTypes.shape({
    street: PropTypes.string,
    zipcode: PropTypes.string,
    locality: PropTypes.string,
  }),
  relayPoint: PropTypes.shape({
    name: PropTypes.string,
    street: PropTypes.string,
    locality: PropTypes.string,
  }),
  transporter: PropTypes.string,
  vertical: PropTypes.bool,
  selectedDates: PropTypes.shape({}),
  clothesPackage: PropTypes.arrayOf(PropTypes.shape({})),
  hasDefinedPrice: PropTypes.bool,
  totalClothes: PropTypes.number.isRequired,
  hasUpcyclingWithoutPriceRange: PropTypes.bool,
  promoCode: PropTypes.shape({
    doesTilliOfferDeliveryFees: PropTypes.bool,
  }),
  deliveryFee: PropTypes.shape({
    packageFee: PropTypes.number,
  }),
  isDoubleProcess: PropTypes.bool,
  rawClothes: PropTypes.arrayOf(PropTypes.shape({})),
  deleteCloth: PropTypes.func.isRequired,
  duplicateCloth: PropTypes.func,
  editingClothIndex: PropTypes.number,
  setEditingClothIndex: PropTypes.func,
  isAdviceOrder: PropTypes.bool,
  pricingInverse: PropTypes.shape({}).isRequired,
  discounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  lastStep: PropTypes.bool,
  isOnlyRemaillage: PropTypes.bool,
  transporterInsurance: PropTypes.shape({
    amount: PropTypes.number,
  }),
  currency: PropTypes.string,
};

OrderSummaryPackage.defaultProps = {
  selectedAddress: {},
  relayPoint: {},
  transporter: null,
  vertical: false,
  selectedDates: {},
  clothesPackage: [],
  hasDefinedPrice: false,
  hasUpcyclingWithoutPriceRange: false,
  promoCode: {},
  deliveryFee: {},
  isDoubleProcess: false,
  rawClothes: [],
  duplicateCloth: () => {},
  setEditingClothIndex: () => {},
  isAdviceOrder: false,
  lastStep: false,
  isOnlyRemaillage: false,
  transporterInsurance: null,
  editingClothIndex: null,
  currency: 'EUR',
};
